import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmSalaries } from '../../../utils/crmPermissions';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import { formatDate } from '../../../utils/appDateTime';

export default function CrmPaymentHistory() {
  const { user } = useAuth();
  const isAdmin = canManageCrmSalaries(user?.role_slug);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [payRow, setPayRow] = useState(null);
  const [form, setForm] = useState({
    payment_method: 'bank_transfer',
    transaction_id: '',
    payment_notes: '',
  });

  const load = async () => {
    try {
      const res = await api.get('/crm/salaries');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markPaid = async () => {
    if (!payRow) return;
    try {
      await api.put(`/crm/salaries/${payRow.id}/payment-status`, {
        payment_status: 'paid',
        ...form,
      });
      toast({ title: 'Marked paid' });
      setPayRow(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const markFailed = async (id) => {
    try {
      await api.put(`/crm/salaries/${id}/payment-status`, { payment_status: 'failed' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  return (
    <>
      <Card>
        <CardHeader><CardTitle className="text-base">Payment History</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Employee</th>
                <th>Period</th>
                <th>Net</th>
                <th>Status</th>
                <th>Method</th>
                <th>Txn</th>
                <th>Paid At</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="py-2">{row.first_name} {row.last_name}</td>
                  <td>{formatDate(row.period_start)} – {formatDate(row.period_end)}</td>
                  <td>₹{Number(row.net_salary || row.final_salary || 0).toLocaleString('en-IN')}</td>
                  <td>{row.display_payment_status || row.payment_status}</td>
                  <td>{row.payment_method || '—'}</td>
                  <td>{row.transaction_id || '—'}</td>
                  <td>{row.paid_at ? formatDate(row.paid_at) : '—'}</td>
                  <td className="whitespace-nowrap space-x-1">
                    {isAdmin && row.payment_status !== 'paid' && (
                      <>
                        <Button size="sm" onClick={() => { setPayRow(row); setForm({ payment_method: 'bank_transfer', transaction_id: '', payment_notes: '' }); }}>
                          Pay
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => markFailed(row.id)}>Fail</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!payRow} onOpenChange={(o) => !o && setPayRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transaction ID</Label>
              <Input value={form.transaction_id} onChange={(e) => setForm((f) => ({ ...f, transaction_id: e.target.value }))} />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.payment_notes} onChange={(e) => setForm((f) => ({ ...f, payment_notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayRow(null)}>Cancel</Button>
            <Button onClick={markPaid}>Confirm Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
